import {
	CalendarDateTime,
	getLocalTimeZone,
	now,
	parseAbsolute,
	toZoned,
	ZonedDateTime,
} from '@internationalized/date'
import { ReactNode, useMemo, useState } from 'react'
import {
	Label,
	type DatePickerProps,
	Calendar,
	CalendarCell,
	CalendarGrid,
	DateInput,
	DatePicker as AriaDatePicker,
	DateSegment,
	Dialog,
	Group,
	Heading,
	Popover,
	CalendarGridHeader,
	CalendarHeaderCell,
	CalendarGridBody,
} from 'react-aria-components'
import { parse } from '../datemath'
import { Button } from './button'

type Props = Omit<DatePickerProps<ZonedDateTime>, 'value' | 'defaultValue' | 'onChange'> & {
	value?: string
	defaultValue?: string
	label?: ReactNode
	onChange?: (value: string) => void
}

function parseValue(value?: string, defaultValue?: string) {
	if (!value && !defaultValue) return null
	const maybeDate = parse(value ?? defaultValue ?? '')
	if (maybeDate.isValid) return maybeDate.toISO()
	return ''
}

export function DatePicker({
	label,
	className,
	name,
	defaultValue,
	value,
	onChange,
	...props
}: Props) {
	const [localValue, setLocalValue] = useState(() => parseValue(value, defaultValue))

	const raValue = useMemo<ZonedDateTime | null>(() => {
		if (!localValue) return null
		return parseAbsolute(localValue, getLocalTimeZone())
	}, [localValue])

	const raOnChange: DatePickerProps<ZonedDateTime | CalendarDateTime>['onChange'] = value => {
		const nextValue = localValue
			? (value as ZonedDateTime)
			: toZoned(value.set({ hour: new Date().getHours() }), getLocalTimeZone())
		const asString = nextValue.toAbsoluteString()
		setLocalValue(asString)
		onChange?.(asString)
	}

	return (
		<>
			<input type="hidden" name={name} value={localValue ?? ''} />
			<AriaDatePicker<ZonedDateTime>
				granularity="minute"
				value={raValue}
				onChange={raOnChange}
				className={className}
				{...props}
			>
				{label && (
					<Label className="flex gap-1 font-normal leading-tight text-xs mb-1">
						{label}
					</Label>
				)}
				<Group
					className={`flex ${props.isRequired ? '[&_div]:invalid:border-red-600 [&_button]:invalid:border-red-600' : ''}`}
				>
					<DateInput className="flex bg-transparent text-sm text-gray-700 font-sans font-normal outline outline-0 disabled:border-gray-100 border border-gray-400 rounded-l focus:border-gray-900 w-full p-2">
						{segment =>
							segment.type === 'literal' && segment.text === ' ' ? (
								<span className="w-2" />
							) : (
								<DateSegment segment={segment} />
							)
						}
					</DateInput>
					<Button variant="INVISIBLE" className="rounded-l-none">
						▼
					</Button>
				</Group>
				<Popover className="bg-white rounded border border-gray-200 shadow-xl shadow-gray-200 p-2 overflow-auto">
					<Dialog className="flex gap-4">
						<Calendar>
							<header className="flex justify-between">
								<Button slot="previous" variant="TEXT" size="SMALL">
									◀
								</Button>
								<Heading />
								<Button slot="next" variant="TEXT" size="SMALL">
									▶
								</Button>
							</header>
							<CalendarGrid>
								<CalendarGridHeader className="bg-blue-50">
									{day => (
										<CalendarHeaderCell className="p-1 text-gray-800 text-sm text-center uppercase font-light border-slate-200">
											{day}
										</CalendarHeaderCell>
									)}
								</CalendarGridHeader>
								<CalendarGridBody>
									{date => (
										<CalendarCell
											date={date}
											className={
												'w-8 h-8 p-1 grid place-items-center text-sm rounded-full outline-none hover:bg-blue-200 selected:bg-blue-400 selected:text-white disabled:text-gray-300'
											}
										/>
									)}
								</CalendarGridBody>
							</CalendarGrid>
						</Calendar>
						<ul className="flex flex-col gap-1 min-w-16 text-right">
							{presets.map((preset, index) => (
								<li key={index}>
									<Button
										variant="TEXT"
										size="TEXT"
										onPress={() => raOnChange(preset.value())}
									>
										{preset.label}
									</Button>
								</li>
							))}
						</ul>
					</Dialog>
				</Popover>
			</AriaDatePicker>
		</>
	)
}

const presets = [
	{
		label: 'Nyní',
		value: () => now(getLocalTimeZone()).set({ minute: 0, second: 0, millisecond: 0 }),
	},
	{
		label: 'Dnes',
		value: () => now(getLocalTimeZone()).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
	},
	{
		label: 'Zítra',
		value: () =>
			now(getLocalTimeZone())
				.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
				.add({ days: 1 }),
	},
	{
		label: 'Pozítří',
		value: () =>
			now(getLocalTimeZone())
				.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
				.add({ days: 2 }),
	},
	{
		label: 'Za týden',
		value: () =>
			now(getLocalTimeZone())
				.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
				.add({ weeks: 1 }),
	},
	{
		label: 'Za měsíc',
		value: () =>
			now(getLocalTimeZone())
				.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
				.add({ months: 1 }),
	},
]
